/**
 * Input Sanitization Utility for Web Admin Portal
 * 
 * Provides functions to sanitize user inputs to prevent XSS attacks
 * and ensure data integrity across the application.
 */

/**
 * Sanitize HTML content by escaping special characters
 * Prevents XSS attacks by converting HTML special chars to entities
 */
export function sanitizeHTML(input: string): string {
  if (typeof input !== 'string') return String(input);
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

interface SanitizeTextOptions {
  maxLength?: number;
  allowNewlines?: boolean;
  allowSpecialChars?: boolean;
}

/**
 * Sanitize text input by removing potentially dangerous characters
 * while preserving normal punctuation and unicode characters
 */
export function sanitizeText(input: string, options: SanitizeTextOptions = {}): string {
  if (typeof input !== 'string') return String(input);
  
  const {
    maxLength = 10000,
    allowNewlines = true
  } = options;
  
  let sanitized = input;
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');
  
  // Remove control characters except newlines and tabs
  if (allowNewlines) {
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  } else {
    sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
  }
  
  // Remove script tags and event handlers
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
}

/**
 * Sanitize email address
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') return '';
  
  // Convert to lowercase and trim
  let sanitized = email.toLowerCase().trim();
  
  // Remove any characters that aren't valid in email addresses
  sanitized = sanitized.replace(/[^a-z0-9@._+-]/g, '');
  
  // Basic email format validation
  const emailRegex = /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (!emailRegex.test(sanitized)) {
    return '';
  }
  
  return sanitized;
}

/**
 * Sanitize username (alphanumeric, underscore, hyphen only)
 */
export function sanitizeUsername(username: string): string {
  if (typeof username !== 'string') return '';
  
  // Convert to lowercase and trim
  let sanitized = username.toLowerCase().trim();
  
  // Remove any characters that aren't alphanumeric, underscore, or hyphen
  sanitized = sanitized.replace(/[^a-z0-9_-]/g, '');
  
  // Limit length
  if (sanitized.length > 50) {
    sanitized = sanitized.substring(0, 50);
  }
  
  return sanitized;
}

/**
 * Sanitize name (letters, spaces, hyphens, apostrophes only)
 */
export function sanitizeName(name: string): string {
  if (typeof name !== 'string') return '';
  
  let sanitized = name.trim();
  
  // Remove any characters that aren't letters, spaces, hyphens, or apostrophes
  // Allow unicode letters for international names
  sanitized = sanitized.replace(/[^\p{L}\s'-]/gu, '');
  
  // Remove multiple consecutive spaces
  sanitized = sanitized.replace(/\s+/g, ' ');
  
  // Limit length
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100);
  }
  
  return sanitized;
}

interface SanitizeNumberOptions {
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  allowNegative?: boolean;
}

/**
 * Sanitize numeric input
 */
export function sanitizeNumber(input: any, options: SanitizeNumberOptions = {}): number | null {
  const {
    min = -Infinity,
    max = Infinity,
    allowDecimals = true,
    allowNegative = true
  } = options;
  
  // Convert to number
  let num = Number(input);
  
  // Check if valid number
  if (isNaN(num) || !isFinite(num)) {
    return null;
  }
  
  // Round if decimals not allowed
  if (!allowDecimals) {
    num = Math.round(num);
  }
  
  // Check negative
  if (!allowNegative && num < 0) {
    num = 0;
  }
  
  // Clamp to range
  num = Math.max(min, Math.min(max, num));
  
  return num;
}

/**
 * Sanitize array of strings
 */
export function sanitizeArray(arr: any[], sanitizeFunc: (item: any) => string = sanitizeText): string[] {
  if (!Array.isArray(arr)) return [];
  
  return arr
    .map(item => sanitizeFunc(item))
    .filter(item => item && item.length > 0);
}

/**
 * Sanitize word list (array of words)
 */
export function sanitizeWordList(words: string[] | string): string[] {
  let wordArray: string[];
  
  if (!Array.isArray(words)) {
    // Try to split string by commas or newlines
    if (typeof words === 'string') {
      wordArray = words.split(/[,\n]+/);
    } else {
      return [];
    }
  } else {
    wordArray = words;
  }
  
  return wordArray
    .map(word => {
      if (typeof word !== 'string') return '';
      
      // Trim and lowercase
      let sanitized = word.trim().toLowerCase();
      
      // Remove special characters except hyphens and apostrophes
      sanitized = sanitized.replace(/[^a-z'-]/g, '');
      
      // Remove leading/trailing hyphens or apostrophes
      sanitized = sanitized.replace(/^[-']+|[-']+$/g, '');
      
      return sanitized;
    })
    .filter(word => word.length > 0)
    .filter((word, index, self) => self.indexOf(word) === index); // Remove duplicates
}

/**
 * Sanitize object by applying sanitization to all string values
 */
export function sanitizeObject(
  obj: Record<string, any>,
  fieldSanitizers: Record<string, (value: any) => any> = {}
): Record<string, any> {
  if (typeof obj !== 'object' || obj === null) return obj;
  
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    // Use custom sanitizer if provided
    if (fieldSanitizers[key]) {
      sanitized[key] = fieldSanitizers[key](value);
    }
    // Recursively sanitize nested objects
    else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value, fieldSanitizers);
    }
    // Sanitize arrays
    else if (Array.isArray(value)) {
      sanitized[key] = sanitizeArray(value);
    }
    // Sanitize strings
    else if (typeof value === 'string') {
      sanitized[key] = sanitizeText(value);
    }
    // Pass through other types
    else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Validate and sanitize form data for student creation
 */
export function sanitizeStudentData(data: any) {
  return {
    firstName: sanitizeName(data.firstName || ''),
    lastName: sanitizeName(data.lastName || ''),
    username: sanitizeUsername(data.username || ''),
    password: sanitizeText(data.password || '', { maxLength: 100, allowNewlines: false }),
    classId: sanitizeText(data.classId || '', { maxLength: 100, allowNewlines: false }),
    parentEmail: sanitizeEmail(data.parentEmail || ''),
    ageRating: sanitizeNumber(data.ageRating, { min: 5, max: 18, allowDecimals: false })
  };
}

/**
 * Validate and sanitize form data for class creation
 */
export function sanitizeClassData(data: any) {
  return {
    className: sanitizeName(data.className || ''),
    yearGroup: sanitizeText(data.yearGroup || '', { maxLength: 50, allowNewlines: false }),
    ageRating: sanitizeNumber(data.ageRating, { min: 5, max: 18, allowDecimals: false }),
    teacherId: sanitizeText(data.teacherId || '', { maxLength: 100, allowNewlines: false })
  };
}

/**
 * Validate and sanitize form data for word list creation
 */
export function sanitizeWordListData(data: any) {
  return {
    listName: sanitizeName(data.listName || ''),
    classId: sanitizeText(data.classId || '', { maxLength: 100, allowNewlines: false }),
    words: sanitizeWordList(data.words || [])
  };
}

/**
 * Validate and sanitize form data for teacher/parent registration
 */
export function sanitizeUserRegistrationData(data: any) {
  return {
    email: sanitizeEmail(data.email || ''),
    password: sanitizeText(data.password || '', { maxLength: 100, allowNewlines: false }),
    firstName: sanitizeName(data.firstName || ''),
    lastName: sanitizeName(data.lastName || ''),
    schoolCode: sanitizeText(data.schoolCode || '', { maxLength: 50, allowNewlines: false })
  };
}
